/**
 * Access Service
 *
 * 存取權限服務 - 管理用戶權限檢查
 * 使用 Supabase 作為後端
 */

import { supabase } from '@shared/services/core/supabaseClient'
import relationshipService from '../organization/relationshipService'
import { roleNames, roleLevel, permissionSets } from '@shared/config/roles'
import { authService } from './authService'

// ==================== 類型定義 ====================

/** 權限來源類型 */
export type PermissionSource = 'role' | 'system' | 'inheritance'

/** 權限資訊（含來源） */
export interface PermissionWithSource {
  action: string
  source: PermissionSource
  role?: string
  userOrgRole?: string
  orgFieldRole?: string
}

/** 權限快取項目 */
interface CacheEntry {
  timestamp: number
  data?: unknown
}

/** 權限快取 */
interface PermissionCache {
  permissions: Record<string, CacheEntry>
}

/** ID 類型 */
export type IdType = 'organization' | 'field' | 'user' | null

/** 權限集合類型 */
export type PermissionSetType = 'organization' | 'field'

/** 案場資料 */
export interface FieldInfo {
  id: string
  name?: string
  [key: string]: unknown
}

// ==================== 常數定義 ====================

const CACHE_DURATION = 5 * 60 * 1000 // 緩存持續時間：5 分鐘

// ==================== 服務定義 ====================

export const accessService = {
  cache: {
    permissions: {}
  } as PermissionCache,

  /**
   * 清除快取
   */
  clearCache(): void {
    this.cache = { permissions: {} }
  },

  /**
   * 檢查快取是否有效
   */
  isCacheValid(cacheEntry: CacheEntry | undefined): boolean {
    return !!cacheEntry && (Date.now() - cacheEntry.timestamp < CACHE_DURATION)
  },

  // 角色名稱對照表
  roleNames,

  // 預設權限設定
  permissionSets,

  /**
   * 獲取用戶的權限及其來源
   * @param orgId - 組織ID
   * @param fieldId - 案場ID（可選）
   * @param userId - 用戶ID（可選，默認為當前用戶）
   * @returns 權限列表，每個權限包含動作和來源資訊
   */
  async getUserPermissionsWithSource(
    orgId: string,
    fieldId: string | null = null,
    userId: string | null = null
  ): Promise<PermissionWithSource[]> {
    const targetUserId = userId || await this.getUserId()
    const orgPermissionSet = await this.getPermissionSet('organization')
    const fieldPermissionSet = await this.getPermissionSet('field')
    const permissions = new Map<string, PermissionWithSource>()

    if (!fieldId) {
      // 組織權限檢查
      const orgRoles = await relationshipService.getRolesDirectly(orgId, '', targetUserId)
      orgRoles.roles.forEach((role: string) => {
        if (orgPermissionSet[role]) {
          orgPermissionSet[role].forEach((perm: string) => {
            if (!permissions.has(perm) || permissions.get(perm)?.source === 'system') {
              permissions.set(perm, {
                action: perm,
                source: 'role',
                role: role
              })
            }
          })
        }
      })

      // 添加系統預設權限
      ;['org:view', 'org:view_basic_info'].forEach(perm => {
        if (!permissions.has(perm)) {
          permissions.set(perm, { action: perm, source: 'system' })
        }
      })
    } else {
      // 案場權限檢查
      // 1. 檢查用戶直接的案場角色
      const fieldDirectRoles = await relationshipService.getRolesDirectly(orgId, fieldId, targetUserId)
      if (fieldDirectRoles.roles.length > 0) {
        fieldDirectRoles.roles.forEach((role: string) => {
          if (fieldPermissionSet[role]) {
            fieldPermissionSet[role].forEach((perm: string) => {
              if (!permissions.has(perm) || permissions.get(perm)?.source === 'system') {
                permissions.set(perm, {
                  action: perm,
                  source: 'role',
                  role: role
                })
              }
            })
          }
        })
      }

      // 2. 同時檢查從組織繼承的權限（取最大權限集合）
      {
        const fieldOrgs = await relationshipService.getRoles('field', fieldId)

        // 遍歷每個與該案場有關聯的組織
        for (const org of fieldOrgs.data.organizations) {
          // 如果指定了組織 ID，只檢查該組織；否則遍歷所有關聯組織
          if (orgId && org.id !== orgId) continue

          // 1. 獲取用戶在該組織中的角色
          const userOrgRoles = await relationshipService.getRolesDirectly(org.id, '', targetUserId)

          // 2. 獲取該組織對這個案場的角色
          const orgFieldRoles = await relationshipService.getRolesDirectly(org.id, fieldId, '')

          // 3. 對每個組織角色和案場角色的組合進行權限計算
          for (const userOrgRole of userOrgRoles.roles) {
            for (const orgFieldRole of orgFieldRoles.roles) {
              // 用戶的組織角色等級 ≥ 組織對案場的角色等級時，繼承權限
              // 例：用戶是組織 admin(4)，組織對案場是 member(1) → 4>=1 → 繼承
              // 通過門檻後，使用用戶組織角色對應的案場權限集
              if (roleLevel[userOrgRole] >= roleLevel[orgFieldRole]) {
                const inheritedPermissions = fieldPermissionSet[userOrgRole] || []
                inheritedPermissions.forEach((perm: string) => {
                  if (!permissions.has(perm) || permissions.get(perm)?.source === 'system') {
                    permissions.set(perm, {
                      action: perm,
                      source: 'inheritance',
                      userOrgRole,
                      orgFieldRole
                    })
                  }
                })
              }
            }
          }
        }
      }

      // 添加系統預設權限
      ;['field:view', 'field:view_basic_info'].forEach(perm => {
        if (!permissions.has(perm)) {
          permissions.set(perm, { action: perm, source: 'system' })
        }
      })
    }

    return Array.from(permissions.values())
  },

  /**
   * 獲取用戶的權限列表
   */
  async getUserPermissions(
    orgId: string,
    fieldId: string | null = null,
    userId: string | null = null
  ): Promise<string[]> {
    const permissionsWithSource = await this.getUserPermissionsWithSource(orgId, fieldId, userId)
    return permissionsWithSource.map(p => p.action)
  },

  /**
   * 獲取權限集合
   */
  async getPermissionSet(type: PermissionSetType): Promise<Record<string, string[]>> {
    return this.permissionSets[type] || {}
  },

  /**
   * 檢查是否有權限執行特定操作
   * @param action - 要檢查的權限動作
   * @param orgId - 組織ID
   * @param fieldId - 案場ID（可選）
   * @param userId - 用戶ID（可選，默認為當前用戶）
   * @returns 是否有權限
   */
  async canPerformAction(
    action: string,
    orgId: string,
    fieldId: string | null = null,
    userId: string | null = null
  ): Promise<boolean> {
    const targetUserId = userId || await this.getUserId()

    if (action.startsWith('field:')) {
      const permissions = await this.getUserPermissions(orgId, fieldId, targetUserId)
      return permissions.includes(action)
    } else {
      const permissions = await this.getUserPermissions(orgId, null, targetUserId)
      return permissions.includes(action)
    }
  },

  /**
   * 獲取當前用戶 ID
   * 使用 authService 取得，若尚未就緒則等待
   */
  async getUserId(): Promise<string> {
    // 先檢查 authService.currentUser
    if (authService.currentUser?.uid) {
      return authService.currentUser.uid
    }
    if (authService.currentUser?.id) {
      return authService.currentUser.id
    }

    // 若 auth 尚未就緒，等待初始化完成
    if (!authService.isAuthReady) {
      const user = await authService.ensureAuthReady()
      if (user?.uid) {
        return user.uid
      }
      if (user?.id) {
        return user.id
      }
    }

    // 最後嘗試從 Supabase session 取得
    const session = await authService.getSession()
    if (session?.user?.id) {
      return session.user.id
    }

    throw new Error('No authenticated user')
  },

  /**
   * 獲取案場資訊
   */
  async getFieldInfo(fieldId: string): Promise<FieldInfo> {
    const { data, error } = await supabase
      .from('fields')
      .select('*')
      .eq('id', fieldId)
      .single()

    if (error || !data) {
      throw new Error(`Field ${fieldId} not found`)
    }
    return data as FieldInfo
  },

  /**
   * 檢測 ID 類型
   */
  async detectIdType(id: string): Promise<IdType> {
    try {
      // 檢查是否為組織
      const { data: orgData } = await supabase
        .from('organizations')
        .select('id')
        .eq('id', id)
        .single()
      if (orgData) return 'organization'

      // 檢查是否為案場
      const { data: fieldData } = await supabase
        .from('fields')
        .select('id')
        .eq('id', id)
        .single()
      if (fieldData) return 'field'

      // 檢查是否為用戶
      const { data: userData } = await supabase
        .from('users')
        .select('id')
        .eq('id', id)
        .single()
      if (userData) return 'user'

      return null
    } catch (error) {
      console.error('Error detecting ID type:', error)
      throw error
    }
  }
}

export default accessService
